import { isAlaskaHawaiiDepartureToContiguous48 } from "./akhiIcao";
import {
  effectiveAlaskaHawaiiDeparturePassengers,
  effectiveDomesticSegmentPassengers,
  effectiveInternationalFacilityPassengers,
} from "./calculator";
import {
  DUNLEAVY_Q4_COMPARISON_RATES,
  DUNLEAVY_WORKSHEET_DEFAULT_DOMESTIC_SEGMENT_USD,
} from "./dunleavyQ4ComparisonReference";
import { money } from "./money";
import { taxablePassengerListSummary } from "./taxablePassengers";
import type { FlightLeg, Rates } from "./types";

export type Q4DomesticBreakdownRow = {
  flightDate: string;
  route: string;
  csvPax: number;
  domPaxDisplay: string;
  segments: number;
  rural: boolean;
  effPax: number;
  segmentUnits: number;
  /** Domestic segment tax at email / Comparison shorthand rate ($4.80). */
  domesticTax: number;
  /** Same segment-units × Comparison alternate domestic (worksheet default $5.30 in this build). */
  domesticTaxCy2025: number;
  intlPax: number;
  summary: string;
};

function domesticSegmentTaxUsd(leg: FlightLeg, ratePerSeg: number): number {
  if (leg.excludeFrom720) return 0;
  const akP = effectiveAlaskaHawaiiDeparturePassengers(leg);
  if (
    akP > 0 &&
    !isAlaskaHawaiiDepartureToContiguous48(leg.fromIcao, leg.toIcao)
  ) {
    return 0;
  }
  if (effectiveInternationalFacilityPassengers(leg) > 0) return 0;
  const paxSeg = effectiveDomesticSegmentPassengers(leg);
  if (paxSeg <= 0 || leg.ruralAirportExempt) return 0;
  const segs = Math.max(1, leg.segmentsDomestic);
  return money(segs * paxSeg * ratePerSeg);
}

/** Row-by-row domestic segment math for Q4 `[Time share]` legs (same rules as Form 720). */
export function computeQ4TimeShareDomesticBreakdownRows(
  legs: FlightLeg[],
  /** Passenger $/segment and intl $/person from the Form 720 tab (defaults to email benchmark rates). */
  worksheetRates: Rates = DUNLEAVY_Q4_COMPARISON_RATES,
  /** Alternate domestic $/segment (Comparison “current IRS” column @ $5.30 when passed as alt rate). */
  worksheetDefaultDomesticSegmentUsd: number = DUNLEAVY_WORKSHEET_DEFAULT_DOMESTIC_SEGMENT_USD
): Q4DomesticBreakdownRow[] {
  const altDom = worksheetDefaultDomesticSegmentUsd;
  const out: Q4DomesticBreakdownRow[] = [];
  for (const leg of legs) {
    const paxSeg = effectiveDomesticSegmentPassengers(leg);
    const segs = Math.max(1, leg.segmentsDomestic);
    const akP = effectiveAlaskaHawaiiDeparturePassengers(leg);
    const intlP = effectiveInternationalFacilityPassengers(leg);
    const stackAkToK = isAlaskaHawaiiDepartureToContiguous48(
      leg.fromIcao,
      leg.toIcao
    );
    const segmentUnits =
      leg.ruralAirportExempt ||
      paxSeg <= 0 ||
      intlP > 0 ||
      (akP > 0 && !stackAkToK)
        ? 0
        : segs * paxSeg;
    out.push({
      flightDate: leg.flightDate,
      route: `${leg.fromIcao}→${leg.toIcao}`,
      csvPax: leg.passengerCount,
      domPaxDisplay:
        leg.passengersDomesticSegmentTax === null ? "—" : String(leg.passengersDomesticSegmentTax),
      segments: leg.segmentsDomestic,
      rural: leg.ruralAirportExempt,
      effPax: paxSeg,
      segmentUnits,
      domesticTax: domesticSegmentTaxUsd(
        leg,
        worksheetRates.domesticSegmentUsdPerPassengerPerSegment
      ),
      domesticTaxCy2025: domesticSegmentTaxUsd(leg, altDom),
      intlPax: effectiveInternationalFacilityPassengers(leg),
      summary: taxablePassengerListSummary(leg),
    });
  }
  return out;
}

export function sumDomesticBreakdownRows(rows: Q4DomesticBreakdownRow[]): {
  units: number;
  domesticTax: number;
  domesticTaxCy2025: number;
} {
  let units = 0;
  let domesticTax = 0;
  let domesticTaxCy2025 = 0;
  for (const row of rows) {
    units += row.segmentUnits;
    domesticTax = money(domesticTax + row.domesticTax);
    domesticTaxCy2025 = money(domesticTaxCy2025 + row.domesticTaxCy2025);
  }
  return { units, domesticTax, domesticTaxCy2025 };
}
